import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { client, getPostByURI } from '../../../apollo-client';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { useFplLinkGate } from '@hooks/useFplLinkGate';
import useLoggedOutRedirect from '@hooks/useLoggedOutRedirect';
import { useProfile } from '@hooks/useProfile';
import { getYoastHeadSEO, isPaidMember } from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import WideLayout from '@components/layout/wide';
import { BREAKPOINTS, TEN_MINUTES_IN_MS } from '@lib/constants';

import Paywall from '@components/paywall';
import {
  LeagueEntry,
  fetchMiniLeague,
  fetchMiniLeagueEntry,
  fetchUserMiniLeagues
} from '@api-service/mini-leagues';
import { miniLeagueTransformer } from '@components/Table/table-transformers';
import { resolveSquadRequestState } from '@lib/page-utils/MiniLeagues/resolve-squad-request-state';
import {
  BodyRegular,
  BodyRegularBold,
  CaptionRegular,
  H3
} from '@components/atoms/typography';
import {
  awardTypes,
  getAwardsData,
  getFilteredMiniLeagues,
  selectMiniLeagueTableEntries
} from '@lib/page-utils/MiniLeagues';

import useWindowSize from '@hooks/useWindowSize';
import Button from '@components/atoms/button';
import SegmentedControl from '@components/atoms/segmented-control';
import Input from '@components/atoms/input';
import Select from '@components/atoms/Select';
import LoadingView from '@components/atoms/loading-view';
import { Accordion, AccordionItem, AccordionTrigger } from '@components/atoms';
import {
  AccordionChevron,
  AccordionContent
} from '@components/atoms/accordion';
import MiniLeagueAward from '@components/mini-league/award';
import Table from '@components/Table';
import {
  MiniLeagueColumnAccessors,
  getMiniLeagueColumns
} from '@components/Table/table-columns';
import FFHModal from '@components/modal';
import MiniLeagueTeamCard from '@components/MiniLeagueTeamCard';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import {
  FplLinkGate,
  FplLinkGateProfileError
} from '@components/my-team/FplLinkGate';
import MiniLeagueStats from '@components/mini-league-stats';

const LAST_FPL_ID_STORAGE_KEY = 'miniLeagues.lastSearchedFplId';
const LAST_LEAGUE_STORAGE_KEY = 'miniLeagues.lastSelectedMiniLeague';

// The table paginates silently past its initial page size, so this must stay
// well clear of the largest possible payload (the top 50 plus the user's own
// FPL standings page).
const MINI_LEAGUE_TABLE_MAX_ROWS = 500;

const readLocalStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // best-effort persistence; ignore quota or access errors
  }
};

const MiniLeaguesPage = ({ post }) => {
  const router = useRouter();
  const { user, isLoading: userIsLoading } = useAuth0User();
  const { accessToken } = useAccessToken();
  const { profile, isLoading: profileIsLoading } = useProfile();
  const { status: linkGateStatus, refetchProfile } = useFplLinkGate();

  const { isRedirecting } = useLoggedOutRedirect({
    router,
    redirectPath: 'ai-team-rating',
    user,
    userIsLoading,
    mobileOnly: true
  });
  const { pathname } = router;
  const { context } = useLeagueContext();
  const currentGameweek = context?.currentGameweek?.number ?? null;
  const nextGameweek = context?.nextGameweek?.number ?? null;

  const [isPayingMember, setIsPayingMember] = useState(false);
  const [fplId, setFplId] = useState<string>('');
  const [fplIdTextInput, setFplIdTextInput] = useState<string>('');

  const [selectedMiniLeague, setSelectedMiniLeague] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const miniLeagueIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.miniLeagueIsClosed,
    false
  );

  const [selectedTab, setSelectedTab] = useState(1);
  const [selectedEntry, setSelectedEntry] = useState<LeagueEntry | null>(null);

  // Bootstrap FPL ID input from (in priority order):
  // 1. last-searched preference in localStorage (per-browser persistence)
  // 2. the user's saved FPL team ID on their ffh-backend profile
  useEffect(() => {
    const lastSearched = readLocalStorage(LAST_FPL_ID_STORAGE_KEY);
    if (lastSearched) {
      setFplId(lastSearched);
      setFplIdTextInput(lastSearched);
      return;
    }
    if (profile?.fplTeamId) {
      const fplTeamId = String(profile.fplTeamId);
      setFplId(fplTeamId);
      setFplIdTextInput(fplTeamId);
    }
  }, [profile?.fplTeamId]);

  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);

  useEffect(() => {
    const hasPaidMembership = isPaidMember(user);
    if (hasPaidMembership) {
      setIsPayingMember(true);
    }
  }, [user]);

  const persistFplId = (next: string) => {
    if (next) writeLocalStorage(LAST_FPL_ID_STORAGE_KEY, next);
    setFplId(next);
  };

  const saveSelectedMiniLeague = (
    option: {
      label: string;
      value: number;
    } | null
  ) => {
    setSelectedMiniLeague(option);
    if (option?.value) {
      writeLocalStorage(LAST_LEAGUE_STORAGE_KEY, String(option.value));
    }
  };

  const setLastLeagueInArray = (response: { id: number; name: string }[]) => {
    const last = response[response.length - 1];
    if (!last) return;
    setSelectedMiniLeague({ label: last.name, value: last.id });
  };

  const { data: miniLeagueData, isLoading: fetchUserMiniLeaguesIsLoading } =
    useQuery({
      queryKey: ['fetchUserMiniLeagues', fplId, accessToken],
      queryFn: async () => {
        if (!accessToken) return [];
        const data = await fetchUserMiniLeagues(accessToken);
        return data.filter(({ leagueType }) => leagueType !== 's');
      },
      enabled: Boolean(fplId) && Boolean(accessToken) && nextGameweek !== 1,
      onSuccess: (response) => {
        if (!response?.length) return;

        const lastSelected = readLocalStorage(LAST_LEAGUE_STORAGE_KEY);
        const lastSelectedId = lastSelected ? Number(lastSelected) : null;

        if (lastSelectedId) {
          const savedLeague = response.find(
            (league) => league.id === lastSelectedId
          );
          if (savedLeague) {
            setSelectedMiniLeague({
              label: savedLeague.name,
              value: savedLeague.id
            });
            return;
          }
        }
        setLastLeagueInArray(response);
      },
      refetchOnWindowFocus: false,
      refetchInterval: TEN_MINUTES_IN_MS
    });

  const { data: singleMiniLeagueData, isLoading: fetchMiniLeagueIsLoading } =
    useQuery({
      queryKey: ['fetchMiniLeague', selectedMiniLeague?.value, accessToken],
      queryFn: async () => {
        if (!selectedMiniLeague?.value || !accessToken) return null;
        return fetchMiniLeague(selectedMiniLeague.value, accessToken);
      },
      enabled:
        Boolean(fplId) &&
        Boolean(accessToken) &&
        nextGameweek !== 1 &&
        Boolean(selectedMiniLeague?.value),
      refetchOnWindowFocus: false,
      refetchInterval: TEN_MINUTES_IN_MS
    });

  const tableData = useMemo(
    () =>
      miniLeagueTransformer(selectMiniLeagueTableEntries(singleMiniLeagueData)),
    [singleMiniLeagueData]
  );

  const { ownershipStats, influenceStats, threatStats } = useMemo(() => {
    const stats = singleMiniLeagueData?.stats;
    if (!stats)
      return { ownershipStats: [], influenceStats: [], threatStats: [] };

    return {
      ownershipStats: stats.ownership,
      influenceStats: stats.importance.filter(
        (player) => player.percentage >= 0
      ),
      threatStats: stats.importance
        .filter((player) => player.percentage < 0)
        .map((player) => ({
          ...player,
          percentage: Math.abs(player.percentage)
        }))
        .reverse()
    };
  }, [singleMiniLeagueData]);

  const { isAppView } = useIsAppView();
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINTS.xs;

  const awardsData = getAwardsData(singleMiniLeagueData?.entries);

  // Re-resolve the selected entry from the latest payload so an open modal
  // reflects data landing from the live poll, not a snapshot from click time.
  const listEntry = selectedEntry
    ? singleMiniLeagueData?.entries.find(
        (entry) => entry.teamId === selectedEntry.teamId
      ) ?? selectedEntry
    : null;

  // A league sent without squads (served by the ranking engine) carries none
  // in its rows, so the opened row's squad is read on its own.
  const hasSquads = singleMiniLeagueData?.hasSquads ?? true;
  const {
    data: fetchedModalEntry,
    isFetching: isModalSquadFetching,
    isError: isModalSquadError,
    refetch: refetchModalSquad
  } = useQuery({
    queryKey: [
      'fetchMiniLeagueEntry',
      selectedMiniLeague?.value,
      selectedEntry?.teamId,
      accessToken
    ],
    queryFn: async () =>
      selectedMiniLeague && selectedEntry && accessToken
        ? fetchMiniLeagueEntry(
            selectedMiniLeague.value,
            selectedEntry.teamId,
            accessToken
          )
        : null,
    enabled:
      !hasSquads &&
      Boolean(selectedEntry) &&
      Boolean(selectedMiniLeague?.value) &&
      Boolean(accessToken),
    refetchOnWindowFocus: false,
    refetchInterval: TEN_MINUTES_IN_MS
  });
  const modalEntry = hasSquads ? listEntry : fetchedModalEntry ?? listEntry;
  const { isSquadLoading, isSquadError } = resolveSquadRequestState({
    hasSquads,
    isEntrySelected: Boolean(selectedEntry),
    hasData: Boolean(fetchedModalEntry),
    isFetching: isModalSquadFetching,
    isError: isModalSquadError
  });

  const canShowData =
    !fetchMiniLeagueIsLoading &&
    !fetchUserMiniLeaguesIsLoading &&
    Boolean(singleMiniLeagueData);

  // Logged-out mobile web visitors are being pushed to the GYTR flow; show a
  // loader instead of flashing the holding message or paywall mid-navigation.
  if (isRedirecting) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <div className="flex justify-center items-center py-10 bg-white">
          <LoadingView
            headerText="Loading"
            loadingText="Taking you to AI Team Rating..."
            progressBarSeconds={8}
          />
        </div>
      </>
    );
  }

  if (miniLeagueIsClosed || nextGameweek === 1) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <EndOfSeasonHoldingMessage html="This feature will give you live mini-league rankings and awards - you'll be able to check it out once the season has started. In the meantime, use our AI to <a href='https://www.fantasyfootballhub.co.uk/my-team/pick'>get your team rated</a> and <a href='https://www.fantasyfootballhub.co.uk/my-team/transfers'>suggest changes</a>." />
      </>
    );
  }

  if (!isPayingMember) {
    return (
      <div className="container h-screen pt-12 px-5 md:px-16">
        <Paywall variant="embedded" title="🔒 Unlock exclusive content" />
      </div>
    );
  }

  if (profileIsLoading || linkGateStatus === 'loading') {
    return (
      <div className="flex justify-center items-center py-10 bg-white">
        <LoadingView
          headerText="Loading"
          loadingText="Getting your profile info"
          progressBarSeconds={8}
        />
      </div>
    );
  }

  if (linkGateStatus === 'error') {
    return (
      <div className="container bg-white">
        <FplLinkGateProfileError onRetry={refetchProfile} />
      </div>
    );
  }

  if (linkGateStatus === 'gate') {
    return (
      <div className="container bg-white">
        <FplLinkGate />
      </div>
    );
  }

  return (
    <>
      <Head>
        {!yoastHead ? (
          <title>{`My Team FPL - Mini League Analyzer`}</title>
        ) : (
          yoastHead
        )}
      </Head>
      <WideLayout className="bg-white px-2" content={false}>
        {isPayingMember && (
          <div
            className={`bg-white pb-6 ${
              fplId && singleMiniLeagueData ? 'pt-1' : ''
            } ${isAppView ? 'mb-4' : '0'}`}
          >
            {fplId && singleMiniLeagueData && (
              <div>
                <SegmentedControl
                  tabLabels={['Awards', 'League Table', 'Stats']}
                  selectedTab={selectedTab}
                  setSelectedTab={(index) => {
                    setSelectedTab(index);
                  }}
                  dataCyValues={[
                    'mini-league-awards-view',
                    'mini-league-league-table-view',
                    'mini-league-gameweek-stats-view'
                  ]}
                />
              </div>
            )}
            <div className="flex flex-col md:flex-row items-start md:items-end space-y-3 pt-2 pb-5">
              <div className="flex flex-col md:flex-row space-y-3 items-start md:items-end">
                <Input
                  label="Your FPL ID"
                  name="fpl-id"
                  value={fplIdTextInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      persistFplId(e.target.value);
                    }
                  }}
                  onChange={(e) => {
                    setFplIdTextInput(e.target.value);
                  }}
                />
                <Button
                  className="ml-2"
                  variant="primary"
                  onClick={() => {
                    persistFplId(fplIdTextInput);
                  }}
                >
                  Search
                </Button>
                {miniLeagueData && (
                  <div className="ml-0 md:ml-10 flex space-y-3">
                    <Select
                      label="Select mini league"
                      className="min-w-[300px]"
                      name="mini-league-selector"
                      value={selectedMiniLeague}
                      options={getFilteredMiniLeagues(miniLeagueData)?.map(
                        ({ name, id }) => ({
                          value: id,
                          label: name
                        })
                      )}
                      onChange={(option) =>
                        saveSelectedMiniLeague(
                          option as { label: string; value: number } | null
                        )
                      }
                      dataCy="mini-league-select"
                    />
                  </div>
                )}
              </div>
            </div>

            {fplId &&
              (fetchUserMiniLeaguesIsLoading || fetchMiniLeagueIsLoading) &&
              isPayingMember && (
                <div className="flex justify-center items-center py-10 bg-white">
                  <LoadingView
                    headerText="Loading"
                    loadingText="Getting your mini league data.."
                    progressBarSeconds={8}
                  />
                </div>
              )}

            {selectedTab === 0 && canShowData && awardsData && (
              <>
                <div className="max-w-[1000px] py-1">
                  <Accordion type="single" className="w-full" collapsible>
                    <div>
                      {/* An award no entry holds a value for is left out:
                          engine-served leagues lack some columns for now. */}
                      {awardTypes
                        .filter(({ key }) => awardsData[key]?.length > 0)
                        .map(({ label, key, emoji }, index) => {
                          return (
                            <AccordionItem
                              key={`award_item_${index}`}
                              value={`${index}`}
                              className="my-4 bg-neutral-100 rounded-lg px-2"
                            >
                              <AccordionTrigger>
                                <div className="flex justify-between w-full mr-2">
                                  <div className="flex items-center">
                                    <BodyRegular>{emoji}</BodyRegular>
                                    <div className="flex flex-col justify-center ml-2 mr-3">
                                      <BodyRegularBold>{key}</BodyRegularBold>
                                      <CaptionRegular>{label}</CaptionRegular>
                                    </div>
                                  </div>
                                  <div className="flex space-x-3">
                                    <div className="bg-primary-100 rounded-lg px-2 py-1 items-center flex">
                                      <BodyRegular className="flex items-center text-xs md:text-base">
                                        <span className="text-xs mr-1 hidden md:flex">
                                          🥇
                                        </span>
                                        {awardsData?.[key]?.[0]?.playerName}
                                      </BodyRegular>
                                    </div>
                                    {awardsData?.[key]?.[1] && (
                                      <div className="hidden md:flex bg-primary-100 rounded-lg px-2 py-1">
                                        <BodyRegular className="flex items-center">
                                          <span className="text-xs mr-1 hidden md:flex ">
                                            🥈
                                          </span>
                                          {awardsData?.[key]?.[1]?.playerName}
                                        </BodyRegular>
                                      </div>
                                    )}
                                    {awardsData?.[key]?.[2] && (
                                      <div className="hidden md:flex bg-primary-100 rounded-lg px-2 py-1">
                                        <BodyRegular className="flex items-center">
                                          <span className="text-xs mr-1 hidden md:flex ">
                                            🥉
                                          </span>
                                          {awardsData?.[key]?.[2]?.playerName}
                                        </BodyRegular>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <AccordionChevron aria-hidden />
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="max-w-[400px]">
                                  <MiniLeagueAward
                                    managerName={
                                      awardsData?.[key]?.[0]?.playerName
                                    }
                                    teamName={awardsData?.[key]?.[0]?.teamName}
                                    awardExplainer={label}
                                    awardName={key}
                                    currentGameweek={currentGameweek}
                                  />
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                    </div>
                  </Accordion>
                </div>
              </>
            )}
            {selectedTab === 1 && canShowData && (
              <div className="flex items-center space-x-3 my-3 shadow-100 rounded-lg">
                <div className="overflow-x-scroll scrollbar-hide">
                  <Table
                    data={tableData ?? []}
                    columns={
                      getMiniLeagueColumns({
                        isMobile,
                        setSelectedMinileagueTeam: setSelectedEntry
                      }) ?? []
                    }
                    hiddenColumns={[MiniLeagueColumnAccessors.teamId]}
                    showFilters={false}
                    initialPageSize={MINI_LEAGUE_TABLE_MAX_ROWS}
                    initialPageIndex={0}
                    highlightRowProperties={{
                      rowValueKey: MiniLeagueColumnAccessors.teamId,
                      comparisonValue: String(profile?.fplTeamId ?? '')
                    }}
                    shouldScrollHighlightRowIntoView
                  />
                </div>
              </div>
            )}
            {selectedTab === 2 && canShowData && (
              <div>
                <H3 className="px-3">Gameweek {currentGameweek} Stats</H3>
                <MiniLeagueStats
                  captainStats={singleMiniLeagueData?.stats.captaincy}
                  ownershipStats={ownershipStats}
                  influenceStats={influenceStats}
                  threatStats={threatStats}
                  breakpoints={{
                    640: { slidesPerView: 1.2, spaceBetween: 16 },
                    768: { slidesPerView: 2.2, spaceBetween: 16 },
                    992: { slidesPerView: 3.2, spaceBetween: 16 }
                  }}
                  captainSubHeader="The percentage of your mini-league rivals who have chosen a particular player as their captain."
                  influenceSubHeader="Represents how much of an impact your players have on your mini-league rivals. The higher the percentage, the more unique or influential that player is in your team compared to others."
                  threatSubHeader="Identifies players who are highly owned by your mini-league rivals but are not in your squad. These players pose a risk because if they perform well, your rivals will gain points that you miss out on."
                  ownershipSubHeader="Shows the percentage of mini-league members who own a particular player. The higher the percentage, the more common that player is across teams in your mini-league."
                />
              </div>
            )}
          </div>
        )}
        <FFHModal
          open={Boolean(selectedEntry)}
          handleClose={() => setSelectedEntry(null)}
        >
          <MiniLeagueTeamCard
            entry={modalEntry}
            onClose={() => setSelectedEntry(null)}
            isSquadLoading={isSquadLoading}
            isSquadError={isSquadError}
            onRetry={() => void refetchModalSquad()}
          />
        </FFHModal>
      </WideLayout>
    </>
  );
};

export default MiniLeaguesPage;

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/fpl-mini-leagues')}
    `
  });

  const post = data.postBy;

  return {
    props: { post }
  };
};
